import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2, Building2, Lock, Mail, ArrowLeft } from "lucide-react";

// On the partners.* subdomain every path routes into the portal, so links to the
// public marketing pages must point at the apex domain with a full navigation.
const PUBLIC_ORIGIN =
  typeof window !== "undefined" && window.location.hostname.startsWith("partners.")
    ? `${window.location.protocol}//${window.location.hostname.slice("partners.".length)}`
    : "";

const PawMark = () => (
  <span className="grid place-items-center w-12 h-12 rounded-2xl bg-paw-primary shadow-[0_8px_20px_-8px_rgba(15,98,214,0.7)] mx-auto">
    <svg viewBox="0 0 24 24" fill="#fff" className="w-7 h-7" aria-hidden="true">
      <circle cx="6" cy="10" r="2.1" /><circle cx="10.5" cy="6.5" r="2.1" /><circle cx="15.5" cy="6.5" r="2.1" /><circle cx="19" cy="10.5" r="1.9" />
      <path d="M12.5 12c-2.4 0-4.4 1.7-5 3.8-.5 1.7.9 3.2 2.7 3.2.9 0 1.6-.4 2.3-.4s1.4.4 2.3.4c1.8 0 3.2-1.5 2.7-3.2-.6-2.1-2.6-3.8-5-3.8z" />
    </svg>
  </span>
);

export default function PartnerLogin({ onLogin }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      if (!["insurance_partner", "admin"].includes(u.role)) { setError("This account does not have partner access."); return; }
      onLogin?.();
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-paw-cream flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <PawMark />
          <p className="text-xs text-paw-primary tracking-[0.2em] uppercase font-bold flex items-center justify-center gap-2 mt-5"><Building2 className="w-3.5 h-3.5" /> Partner Portal</p>
          <p className="text-sm text-paw-text-secondary mt-2">Submit and track member referrals</p>
        </div>
        <form onSubmit={submit} className="bg-white border border-paw-border rounded-3xl p-8 space-y-5 shadow-sm">
          {error && <p className="text-sm text-paw-error bg-paw-error/10 border border-paw-error/20 rounded-xl px-4 py-2.5">{error}</p>}
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-paw-navy">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-paw-text-muted absolute left-4 top-1/2 -translate-y-1/2" />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus className="w-full h-11 pl-11 pr-4 rounded-xl bg-white border border-paw-border text-paw-text text-sm focus:outline-none focus:ring-2 focus:ring-paw-primary/30 focus:border-paw-primary" />
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-paw-navy">Password</label>
              <a href={`${PUBLIC_ORIGIN}/forgot-password`} className="text-xs font-semibold text-paw-primary hover:underline">Forgot password?</a>
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-paw-text-muted absolute left-4 top-1/2 -translate-y-1/2" />
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full h-11 pl-11 pr-4 rounded-xl bg-white border border-paw-border text-paw-text text-sm focus:outline-none focus:ring-2 focus:ring-paw-primary/30 focus:border-paw-primary" />
            </div>
          </div>
          <button type="submit" disabled={loading} className="w-full h-11 bg-paw-primary hover:bg-paw-primary-hover disabled:opacity-50 text-white rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </button>
          <p className="text-xs text-paw-text-muted text-center">Not a partner yet? <a href={`${PUBLIC_ORIGIN}/for-insurance/apply`} className="text-paw-primary font-semibold">Apply here</a></p>
        </form>
        <a href={`${PUBLIC_ORIGIN}/for-insurance`} className="mt-6 flex items-center justify-center gap-1.5 text-sm text-paw-text-secondary hover:text-paw-navy transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to PawLinx for partners
        </a>
      </div>
    </div>
  );
}
